<script>
  import controller from '@/controller';
  import { decodeTweetText } from '@/util/decodeTweetText';
  import * as linkify from 'linkifyjs';
  import { validateLink } from '@/util/validateLink';
  import RetweetMixin from '@/views/Mixins/RetweetMixin.vue';
  import lodash from 'lodash';

  export default {
    data() {
      return {
        quoteURL: null,
        quoteTweetPreview: null,
        initialLoad: false,
        isFetchingQuoteTweetData: false,
        hasVisibleQuoteURL: false,
      };
    },
    mounted() {
      const quoteTweetData = lodash.get(this, 'tweet.quoteTweetData');
      const visibleQuoteURL = this.getQuoteTweetURLFromText(lodash.get(this, 'tweet.status', ''));

      if (visibleQuoteURL) {
        this.hasVisibleQuoteURL = true;
      }

      if (quoteTweetData) {
        this.initialLoad = true;
        this.quoteTweetPreview = lodash.omit(quoteTweetData, 'url');
        this.quoteURL = quoteTweetData.url;
      }
    },
    methods: {
      removeQuoteTweet() {
        this.removeVisibleQuoteURLFromStatus();
        this.quoteURL = null;
        this.quoteTweetPreview = null;
        this.hasVisibleQuoteURL = false;
        this.$emit('visible-quote-url-updated', null);
        this.$emit('quote-tweet-data-updated', null);
      },
      removeVisibleQuoteURLFromStatus() {
        const visibleQuoteURL = this.getQuoteTweetURLFromText(lodash.get(this, 'status', ''));

        if (visibleQuoteURL) {
          this.status = this.removeQuoteURLFromText(this.status, visibleQuoteURL);
          this.$emit('status-updated', this.status, lodash.get(this, 'tweet.count'));
          return;
        }

        const replyStatus = lodash.get(this, 'reply.status', '');
        const visibleReplyQuoteURL = this.getQuoteTweetURLFromText(replyStatus);

        if (visibleReplyQuoteURL) {
          this.updateStatus(this.removeQuoteURLFromText(replyStatus, visibleReplyQuoteURL));
          return;
        }

        const tweetStatus = lodash.get(this, 'tweet.status', '');
        const visibleTweetQuoteURL = this.getQuoteTweetURLFromText(tweetStatus);

        if (visibleTweetQuoteURL) {
          this.$emit(
            'status-updated',
            this.removeQuoteURLFromText(tweetStatus, visibleTweetQuoteURL),
            lodash.get(this, 'tweet.count'),
          );
        }
      },
      removeQuoteURLFromText(text, quoteURL) {
        return text
          .replace(quoteURL, '')
          .replace(/[ \t]+\n/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
      },
      getQuoteTweetURLFromText(text) {
        return (text || '')
          .split(/\s+/)
          .find(
            (l) =>
              (l.includes('twitter.com') || l.includes('x.com')) &&
              validateLink(l).isValid &&
              !l.includes('/video/') &&
              l.includes('/status/'),
          );
      },
      formatQuoteTweetURL(link) {
        const linkInfo = linkify.find(link);

        return linkInfo.length
          ? this.formatTweetLink(linkInfo[0].href)
          : this.formatTweetLink(link);
      },
      getFormattedQuoteTweetURLFromText(text) {
        const link = this.getQuoteTweetURLFromText(text);

        return link ? this.formatQuoteTweetURL(link) : null;
      },
      getCurrentVisibleQuoteText() {
        if (lodash.has(this, 'status')) {
          return lodash.get(this, 'status', '');
        }

        if (lodash.has(this, 'reply.status')) {
          return lodash.get(this, 'reply.status', '');
        }

        return lodash.get(this.tweet, 'status', '');
      },
      getCurrentFormattedVisibleQuoteURL() {
        return this.getFormattedQuoteTweetURLFromText(this.getCurrentVisibleQuoteText());
      },
      reconcileVisibleQuoteURLAfterFetch() {
        const formattedVisibleQuoteURL = this.getCurrentFormattedVisibleQuoteURL();

        if (formattedVisibleQuoteURL) {
          this.hasVisibleQuoteURL = true;
          this.$emit('visible-quote-url-updated', formattedVisibleQuoteURL);

          if (formattedVisibleQuoteURL !== this.quoteURL) {
            this.quoteURL = formattedVisibleQuoteURL;
          }

          return;
        }

        if (this.hasVisibleQuoteURL) {
          this.quoteURL = null;
          this.hasVisibleQuoteURL = false;
          this.$emit('visible-quote-url-updated', null);
        }
      },
      setQuoteURLIfValid(text) {
        const formattedQuoteURL = this.getFormattedQuoteTweetURLFromText(text);

        if (formattedQuoteURL) {
          this.hasVisibleQuoteURL = true;
          this.quoteURL = formattedQuoteURL;
          this.$emit('visible-quote-url-updated', formattedQuoteURL);
        } else if (this.hasVisibleQuoteURL) {
          this.quoteURL = null;
          this.$emit('visible-quote-url-updated', null);
        }
      },
      async getQuoteTweetData(link) {
        try {
          const tweetId = link.split('/').pop();
          const result = await controller.getTweet(tweetId, this.currentUser, this.userProfile.uid);
          const tweet = result.data;
          const userFromTwitter = lodash.get(result, 'includes.users', [{}])[0];

          const quoteTweetData = {
            text: decodeTweetText(tweet.text),
            userProfilePictureURL: userFromTwitter.profile_image_url,
            userDisplayName: userFromTwitter.name,
            username: userFromTwitter.username,
            tweetId: tweet.id,
          };

          return {
            ...quoteTweetData,
            url: this.formatQuoteTweetURL(link),
          };
        } catch (error) {
          console.error(error);
          this.swalModal({
            title: 'Sorry',
            text: `An error has occured while trying to fetch the quoted tweet's data.`,
            type: 'warning',
          });
        }
      },
    },
    watch: {
      async quoteURL(val) {
        // When editing the post we already have the quoteTweetData so we don't need to fetch it.
        if (this.initialLoad) {
          this.initialLoad = false;
          return;
        }

        if (!val) {
          this.quoteTweetPreview = null;
          this.$emit('visible-quote-url-updated', null);
          this.$emit('quote-tweet-data-updated', null);
          return;
        }

        this.isFetchingQuoteTweetData = true;

        const quoteTweetData = await this.getQuoteTweetData(val);

        if (this.getCurrentFormattedVisibleQuoteURL() !== val) {
          this.isFetchingQuoteTweetData = false;
          this.reconcileVisibleQuoteURLAfterFetch();
          return;
        }

        this.quoteTweetPreview = lodash.omit(quoteTweetData, 'url');

        this.$emit('quote-tweet-data-updated', quoteTweetData);

        this.isFetchingQuoteTweetData = false;
      },
      tweet: {
        deep: true,
        handler(val) {
          const formattedVisibleQuoteURL = this.getFormattedQuoteTweetURLFromText(
            lodash.get(val, 'status', ''),
          );

          if (formattedVisibleQuoteURL) {
            this.hasVisibleQuoteURL = true;
            this.$emit('visible-quote-url-updated', formattedVisibleQuoteURL);
          }

          if (
            val &&
            val.quoteTweetData &&
            !formattedVisibleQuoteURL &&
            this.hasVisibleQuoteURL &&
            !this.isFetchingQuoteTweetData
          ) {
            this.quoteTweetPreview = null;
            this.quoteURL = null;
            this.hasVisibleQuoteURL = false;
            this.$emit('visible-quote-url-updated', null);
            this.$emit('quote-tweet-data-updated', null);
            return;
          }

          if (
            formattedVisibleQuoteURL &&
            formattedVisibleQuoteURL !== this.quoteURL &&
            !this.isFetchingQuoteTweetData
          ) {
            this.quoteURL = formattedVisibleQuoteURL;
            return;
          }

          if (
            val &&
            val.quoteTweetData &&
            val.quoteTweetData.url !== this.quoteURL &&
            !this.isFetchingQuoteTweetData
          ) {
            this.quoteTweetPreview = lodash.omit(val.quoteTweetData, 'url');
            this.quoteURL = val.quoteTweetData.url;
          }
        },
      },
    },
    mixins: [RetweetMixin],
  };
</script>
